import {
  ApiBlueprint,
  ApiRef,
  createApiFactory,
  createApiRef,
  configApiRef,
  createFrontendModule,
  microsoftAuthApiRef,
  useApi,
} from '@backstage/frontend-plugin-api';
import {
  SignInPageBlueprint,
  type SignInPageProps,
} from '@backstage/plugin-app-react';
import { SignInPage } from '@backstage/core-components';

type MicrosoftAuthApi =
  typeof microsoftAuthApiRef extends ApiRef<infer T> ? T : never;

export const ACCESS_DENIED_MESSAGE =
  'You do not have permission to use this portal. Access is limited to members of the ' +
  'PlatformEngineering_Development, PlatformEngineering_Demo and Admin DevOps groups. ' +
  'If you should have access, ask the DevOps team to add you; new members can take up to an hour to appear.';

// The sign-in resolver rejects anyone who is not a catalog User (only members of the three
// groups are ingested) or who is outside the allowed domains. Backstage words both as a catalog
// configuration problem ("unable to resolve user identity ... verify that your catalog contains
// the expected User entities"), which tells an ordinary user nothing.
const REJECTED = /unable to resolve user identity|expected User entities|not allowed|NotAllowedError|NotFoundError/i;

/** Wraps the Microsoft auth API so a rejected sign-in shows a permission message instead. */
export function withFriendlyAccessDenied(api: MicrosoftAuthApi): MicrosoftAuthApi {
  return new Proxy(api, {
    get(target, prop) {
      if (prop === 'getBackstageIdentity') {
        return async (...args: any[]) => {
          try {
            return await (target as any).getBackstageIdentity(...args);
          } catch (error: any) {
            if (REJECTED.test(`${error?.name} ${error?.message}`)) {
              const denied = new Error(ACCESS_DENIED_MESSAGE);
              denied.name = 'AccessDeniedError';
              throw denied;
            }
            throw error;
          }
        };
      }
      const value = Reflect.get(target, prop);
      return typeof value === 'function' ? value.bind(target) : value;
    },
  });
}

export const signInAuthApiRef = createApiRef<MicrosoftAuthApi>({
  id: 'quantios.sign-in.microsoft-auth',
});

const signInAuthApi = ApiBlueprint.make({
  name: 'sign-in-auth',
  params: defineParams =>
    defineParams(
      createApiFactory({
        api: signInAuthApiRef,
        deps: { microsoft: microsoftAuthApiRef },
        factory: ({ microsoft }) => withFriendlyAccessDenied(microsoft),
      }),
    ),
});

const microsoftProvider = {
  id: 'microsoft-auth-provider',
  title: 'Microsoft Entra ID',
  message:
    'Sign in with your Quantios account. Access is limited to members of the Development, Demo and Platform Engineering groups.',
  apiRef: signInAuthApiRef,
};

/**
 * Entra ID is the only provider in every deployed environment. The guest provider is offered
 * only when `auth.environment` is explicitly `development`, which only a developer's gitignored
 * app-config.local.yaml sets (the deployed config sets `production`). The backend guest provider
 * independently refuses to run outside NODE_ENV=development. See local/README.md.
 */
export function signInProviders(authEnvironment: string | undefined) {
  return authEnvironment === 'development'
    ? (['guest', microsoftProvider] as const)
    : ([microsoftProvider] as const);
}

/** Replaces @backstage/plugin-app's default sign-in page, which only offers the guest provider. */
function QuantiosSignInPage(props: SignInPageProps) {
  const authEnvironment = useApi(configApiRef).getOptionalString('auth.environment');
  return <SignInPage {...props} providers={[...signInProviders(authEnvironment)]} />;
}

export const signInModule = createFrontendModule({
  pluginId: 'app',
  extensions: [
    signInAuthApi,
    SignInPageBlueprint.make({
      params: {
        loader: async () => props => <QuantiosSignInPage {...props} />,
      },
    }),
  ],
});
